import { text, integer, primaryKey, index } from "drizzle-orm/sqlite-core";
import { defineTable } from "@kubdee/sdk/schema";
import { marketingContentPosts } from "./content-posts";
import { marketingMediaAssets } from "./media-assets";

// ============================================================================
// marketing_post_media — Junction between content posts and media assets
//
// Many-to-many join. `position` controls carousel ordering — a single image
// post just has one row with position 0; a 5-image carousel has five rows
// (positions 0..4) all sharing the same `post_id`.
//
// No surrogate `id` column — `(post_id, media_id)` is the composite PK. This
// matches the chat module's child-table convention and keeps inserts
// straightforward (no ULID minting needed). Use `position` only for sorting,
// not as part of the key — duplicate positions across reorders are allowed
// transiently and resolved by client-side reconciliation.
//
// Cascade deletion is intentional only on the parent side — when a post is
// hard-deleted, its junction rows go too. Media assets are reusable across
// posts so we never cascade in that direction; orphan-cleanup happens in a
// background job tracked by #791.
// ============================================================================
export const marketingPostMedia = defineTable(
  "marketing",
  "post_media",
  {
    postId: text("post_id")
      .notNull()
      .references(() => marketingContentPosts.id, { onDelete: "cascade" }),
    mediaId: text("media_id")
      .notNull()
      .references(() => marketingMediaAssets.id),
    position: integer("position").notNull().default(0),
  },
  (table) => [
    primaryKey({ columns: [table.postId, table.mediaId] }),
    index("marketing_post_media_media_id_idx").on(table.mediaId),
  ],
);

export type MarketingPostMedia = typeof marketingPostMedia.$inferSelect;
export type NewMarketingPostMedia = typeof marketingPostMedia.$inferInsert;
